import * as RAlertDialog from '@radix-ui/react-alert-dialog'
import classNames from 'classnames'
import { forwardRef, HTMLAttributes, ReactNode } from 'react'

import { BodyText } from '../../body-text/body-text'
import { Button, ButtonProps } from '../../button/button'
import { Heading } from '../../heading/heading'
import { Overlay } from '../../overlay/overlay'
import { commonPreventEscapeKeyDownCondition, PreventEscapeKeyDownCondition } from '../common'
import styles from './confirm.module.scss'

export type ConfirmElement = HTMLDivElement

export interface ConfirmProps extends Omit<HTMLAttributes<ConfirmElement>, 'title'> {
  className?: string
  title?: ReactNode
  description?: ReactNode
  confirmLabel?: string
  confirmProps?: Partial<ButtonProps>
  cancelLabel?: string
  cancelProps?: Partial<ButtonProps>
  open?: boolean
  defaultOpen?: boolean
  danger?: boolean
  onOpenChange?: (open: boolean) => void
  onConfirm?: ButtonProps['onClick']
  onCancel?: ButtonProps['onClick']
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  preventEscapeKeyDownCondition?: PreventEscapeKeyDownCondition<ConfirmProps>
}

export const confirmPreventEscapeKeyDownCondition = commonPreventEscapeKeyDownCondition

export const Confirm = forwardRef<ConfirmElement, ConfirmProps>(function Confirm(
  {
    children,
    className,
    title,
    description,
    confirmLabel = 'Ok',
    confirmProps = {},
    cancelLabel = 'Cancelar',
    cancelProps = {},
    defaultOpen,
    danger,
    onConfirm,
    onCancel,
    onEscapeKeyDown,
    preventEscapeKeyDownCondition = confirmPreventEscapeKeyDownCondition,
    ...rest
  },
  forwardedRef
) {
  const classes = classNames(styles.root, className)

  cancelProps.disabled = cancelProps.disabled ?? confirmProps.loading

  return (
    <RAlertDialog.Root defaultOpen={defaultOpen} {...rest}>
      <RAlertDialog.Trigger asChild>{children}</RAlertDialog.Trigger>
      <RAlertDialog.Portal>
        <RAlertDialog.Overlay asChild>
          <Overlay />
        </RAlertDialog.Overlay>
        <RAlertDialog.Content
          data-testid="content"
          className={classes}
          onEscapeKeyDown={(evt) => {
            onEscapeKeyDown?.(evt)
            if (preventEscapeKeyDownCondition({ cancelProps })) {
              evt.preventDefault()
            }
          }}
          ref={forwardedRef}
        >
          <RAlertDialog.Title asChild>
            <Heading>{title}</Heading>
          </RAlertDialog.Title>
          <RAlertDialog.Description asChild>
            <BodyText className={styles.description}>{description}</BodyText>
          </RAlertDialog.Description>
          <div className={styles.buttonContainer}>
            <RAlertDialog.Action disabled={confirmProps.disabled || confirmProps.loading} asChild>
              <Button {...confirmProps} kind={danger ? 'danger' : 'primary'} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </RAlertDialog.Action>
            <RAlertDialog.Cancel asChild>
              <Button
                {...cancelProps}
                kind="tertiary"
                onClick={onCancel}
                className={classNames(styles.buttonCancel, cancelProps.className)}
              >
                {cancelLabel}
              </Button>
            </RAlertDialog.Cancel>
          </div>
        </RAlertDialog.Content>
      </RAlertDialog.Portal>
    </RAlertDialog.Root>
  )
})
